import { API_URL, AUTH_MODE, DEV_USER, DEV_PASSWORD, DEV_AZIENDA } from "./client"

// Cache in memoria (necessaria in iframe terze parti dove localStorage può essere bloccato)
let tokenCache: string | null = null
let utenteCache: string | null = null
let aziendaCache: string | null = null
// Flag per evitare doppie chiamate a registerkc con lo stesso token
let registerkcDone = false

export function setToken(token: string) {
  if (!token || token === "undefined" || token === "null") {
    tokenCache = null
    try {
      localStorage.removeItem("token")
    } catch {
      // localStorage potrebbe essere bloccato in contesto iframe terze parti
    }
    return
  }
  tokenCache = token
  try {
    localStorage.setItem("token", token)
  } catch {
    // localStorage potrebbe essere bloccato in contesto iframe terze parti
  }
  // Mantieni Zustand store allineato (AuthGuard legge da lì); import dinamico per evitare cicli.
  import("../store/states/useAuthStore").then(({ useAuthStore }) => {
    if (useAuthStore.getState().token !== token) {
      useAuthStore.getState().setAuth(token)
    }
  }).catch(() => { /* store non disponibile in alcuni contesti */ })
}

export function getToken(): string {
  if (tokenCache) return tokenCache
  try {
    const stored = localStorage.getItem("token")
    if (stored && stored !== "undefined" && stored !== "null") {
      tokenCache = stored
      return stored
    }
    if (stored === "undefined" || stored === "null") {
      // Pulisci storage contaminato da setItem precedente con valore undefined
      localStorage.removeItem("token")
    }
  } catch {
    // localStorage potrebbe essere bloccato in contesto iframe terze parti
  }
  return ""
}

export function getUtente(): string {
  if (utenteCache) return utenteCache
  try {
    return localStorage.getItem("username") || ""
  } catch {
    return ""
  }
}

export function getAzienda(): string {
  if (aziendaCache) return aziendaCache
  try {
    return localStorage.getItem("azienda_sysdat") || localStorage.getItem("azienda") || ""
  } catch {
    return ""
  }
}

export function setUtente(utente: string) {
  utenteCache = utente
  try {
    localStorage.setItem("username", utente)
  } catch { /* iframe */ }
}

export function setAzienda(azienda: string) {
  aziendaCache = azienda
  try {
    localStorage.setItem("azienda_sysdat", azienda)
  } catch { /* iframe */ }
}

// Alias per retrocompatibilita'
export const setSid = setToken
export const getSid = getToken

// ============================================================================
// Snapshot del contesto di autenticazione (azienda + utente) al momento dell'ultima
// registrazione del token a backend. Se questi valori cambiano in localStorage durante
// la sessione (es. il portale cambia azienda passando nuovi parametri), il token in
// cache è ancora legato alla vecchia azienda → il backend rifiuta i dati. In tal caso
// forziamo un refresh token (refreshkc + registerkc) per riallineare backend e client.
// ============================================================================
let authStateSnapshot: { azienda: string; utente: string } | null = null

function readCurrentAzienda(): string {
  try {
    return localStorage.getItem("azienda_sysdat") || localStorage.getItem("azienda") || aziendaCache || ""
  } catch {
    return aziendaCache || ""
  }
}

function readCurrentUtente(): string {
  try {
    return localStorage.getItem("username") || utenteCache || ""
  } catch {
    return utenteCache || ""
  }
}

export function saveAuthStateSnapshot(): void {
  authStateSnapshot = {
    azienda: readCurrentAzienda(),
    utente: readCurrentUtente(),
  }
}

function clearAuthStateSnapshot(): void {
  authStateSnapshot = null
}

function hasAuthContextChanged(): boolean {
  if (!authStateSnapshot) return false
  const currentAzienda = readCurrentAzienda()
  const currentUtente = readCurrentUtente()
  // Se i valori correnti sono vuoti (es. localStorage non disponibile in iframe),
  // evitiamo falsi positivi: non sappiamo lo stato reale, non forziamo refresh.
  if (!currentAzienda && !currentUtente) return false
  return currentAzienda !== authStateSnapshot.azienda || currentUtente !== authStateSnapshot.utente
}

// Mutex per evitare refresh concorrenti
let refreshPromise: Promise<string> | null = null

/**
 * Controlla se il contesto di autenticazione (azienda/utente) in localStorage è
 * cambiato rispetto all'ultima registerkc; in tal caso forza un refresh token
 * (refreshkc + registerkc) per riallineare backend e client.
 *
 * Esportato perché va invocato sia da `fetchGuard` (chiamate via fetch) sia
 * dall'interceptor request di axios in `networking.ts` (la maggior parte delle
 * chiamate API di prenotazioni passa da lì).
 *
 * Non lancia: se il refresh fallisce logga e ritorna, lasciando che la 401/403
 * successiva inneschi il retry standard.
 */
export async function ensureFreshAuthContext(): Promise<void> {
  if (!hasAuthContextChanged()) return

  const oldAzienda = authStateSnapshot?.azienda ?? ""
  const oldUtente = authStateSnapshot?.utente ?? ""
  const newAzienda = readCurrentAzienda()
  const newUtente = readCurrentUtente()
  const oldToken = getToken()

  console.group("%c[AUTH] Cambio contesto rilevato → refresh token forzato", "color:#ff9800;font-weight:bold")
  console.warn("Azienda (snapshot salvato) :", oldAzienda)
  console.warn("Azienda (letta da localStorage):", newAzienda)
  console.warn("Utente  (snapshot salvato) :", oldUtente)
  console.warn("Utente  (letto da localStorage):", newUtente)
  console.warn("Token PRIMA della refresh  :", oldToken)
  console.groupEnd()

  try {
    await refreshToken()
    const newToken = getToken()
    console.group("%c[AUTH] Refresh token completata", "color:#4caf50;font-weight:bold")
    console.log("Token DOPO la refresh      :", newToken)
    console.log("Token cambiato?            :", oldToken !== newToken ? "SÌ" : "NO (stesso token)")
    console.log("Azienda ora registrata     :", readCurrentAzienda())
    console.log("Utente  ora registrato     :", readCurrentUtente())
    console.groupEnd()
  } catch (e) {
    console.error("[AUTH] Refresh per cambio contesto FALLITO:", e)
    // Non rilanciamo: lasciamo che la 401/403 successiva inneschi il retry standard
  }
}

export async function fetchGuard(url: string, options?: RequestInit, _retryCount: number = 0): Promise<any> {
  // Controllo cambio contesto auth (azienda/utente) prima di partire con la chiamata.
  const tokenBefore = getToken()
  await ensureFreshAuthContext()
  const tokenAfter = getToken()
  // Se il refresh ha cambiato il token, aggiorna il sid nel body della richiesta.
  if (tokenBefore !== tokenAfter && options?.body) {
    try {
      const bodyObj = JSON.parse(options.body as string)
      if ("sid" in bodyObj) {
        bodyObj.sid = tokenAfter
        options = { ...options, body: JSON.stringify(bodyObj) }
      }
    } catch { /* body non è JSON, prosegui */ }
  }

  const response = await fetch(url, options)
  const responseAsText = await response.clone().text()

  if (!response.ok) {
    if (response.status === 401 || response.status === 403) {
      if (_retryCount >= 1) {
        console.error(`fetchGuard: HTTP ${response.status} anche dopo refresh, abort per evitare loop`)
        throw new Error(`Fetch error (${response.status}) anche dopo refresh: ${responseAsText}`)
      }
      console.warn(`fetchGuard: HTTP ${response.status}, tentativo refresh token (retry ${_retryCount + 1})...`)
      try {
        await refreshToken()
        const retryOptions = { ...options }
        if (retryOptions.body) {
          try {
            const bodyObj = JSON.parse(retryOptions.body as string)
            bodyObj.sid = getToken()
            retryOptions.body = JSON.stringify(bodyObj)
          } catch { /* body non e' JSON, prosegui */ }
        }
        return fetchGuard(url, retryOptions, _retryCount + 1)
      } catch (refreshError) {
        console.error("fetchGuard: refresh fallito:", refreshError)
        throw new Error(`Fetch error (${response.status}): ${responseAsText}`)
      }
    }
    throw new Error(`Fetch error (${response.status}): ${responseAsText}`)
  }

  let jsonResponse
  try {
    jsonResponse = JSON.parse(responseAsText)
  } catch {
    if (responseAsText.trim().startsWith("<")) {
      throw new Error("Invalid API response (HTML received instead of JSON)")
    } else {
      throw new Error("Invalid API response (non-JSON content)")
    }
  }

  if (jsonResponse?.esito === "KO") {
    if (jsonResponse?.messaggio === "Token non valido") {
      if (_retryCount >= 1) {
        console.error("fetchGuard: 'Token non valido' anche dopo refresh, abort per evitare loop")
        throw new Error("Token non valido (anche dopo refresh)")
      }
      console.warn(`fetchGuard: Token non valido, tentativo refresh (retry ${_retryCount + 1})...`)
      try {
        await refreshToken()
        const retryOptions = { ...options }
        if (retryOptions.body) {
          try {
            const bodyObj = JSON.parse(retryOptions.body as string)
            bodyObj.sid = getToken()
            retryOptions.body = JSON.stringify(bodyObj)
          } catch { /* body non e' JSON */ }
        }
        return fetchGuard(url, retryOptions, _retryCount + 1)
      } catch (refreshError) {
        console.error("fetchGuard: refresh dopo token non valido fallito:", refreshError)
      }
    }

    throw new Error(jsonResponse?.messaggio)
  }

  return jsonResponse
}

export async function registerToken(token: string): Promise<string> {
  const utente = getUtente()
  const azienda = getAzienda()

  const response = await fetch(`${API_URL}/registerkc`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ token, utente, azienda }),
  })

  if (!response.ok) throw new Error(`registerkc HTTP ${response.status}`)
  const data = await response.json()
  if (data?.esito === "KO") {
    if (data?.messaggio?.includes("unique key violation")) {
      console.log("registerToken: chiave duplicata, token gia' registrato — uso token esistente")
      setToken(token)
      registerkcDone = true
      saveAuthStateSnapshot()
      return token
    }
    throw new Error(`registerkc: ${data?.messaggio}`)
  }

  const finalToken = data.token || token
  setToken(finalToken)
  registerkcDone = true
  saveAuthStateSnapshot()
  return finalToken
}

let sessionExpiredRedirecting = false

function handleSessionExpired(): never {
  if (!sessionExpiredRedirecting) {
    sessionExpiredRedirecting = true
    console.warn("⚠️ Sessione scaduta, redirect al login...")

    // Pulisci token, lock registerKC e snapshot auth da cache e localStorage
    tokenCache = null
    clearAuthStateSnapshot()
    try {
      localStorage.removeItem("token")
      localStorage.removeItem("registerkc_done_token")
      localStorage.removeItem("registerkc_done_azienda")
      localStorage.removeItem("registerkc_done_utente")
    } catch { /* iframe */ }

    // Pulisci lo store Zustand per triggerare AuthGuard → redirect al login
    try {
      import("../store/states/useAuthStore").then(({ useAuthStore }) => {
        useAuthStore.getState().clearAuth()
      })
    } catch (e) {
      console.error("Impossibile pulire auth store:", e)
    }

    // Reset flag dopo un breve delay per permettere il redirect
    setTimeout(() => { sessionExpiredRedirecting = false }, 3000)
  }
  throw new Error("Sessione scaduta. Effettua nuovamente il login.")
}

export async function refreshToken(): Promise<string> {
  const currentToken = getToken()
  if (!currentToken) return handleSessionExpired()

  try {
    // Tentativo 1: refreshkc + registerkc
    const response = await fetch(`${API_URL}/refreshkc`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token: currentToken }),
    })

    console.log("[AUTH DEBUG] refreshkc HTTP status:", response.status)
    if (!response.ok) throw new Error(`refreshkc HTTP ${response.status}`)
    const data = await response.json()
    console.log("[AUTH DEBUG] refreshkc response body:", JSON.stringify(data))
    if (data?.esito === "KO") throw new Error(`refreshkc KO: ${data?.messaggio || "(messaggio assente)"}`)
    if (!data?.token) throw new Error("refreshkc: token mancante in risposta — response body: " + JSON.stringify(data))

    // refreshkc OK → registra SEMPRE il nuovo token
    registerkcDone = false
    return await registerToken(data.token)
  } catch (refreshError) {
    // Tentativo 2: re-login completo (refreshkc o registerkc falliti)
    console.warn("refreshToken fallito, tentativo re-login:", refreshError)

    try {
      registerkcDone = false
      await login(true)
      const newToken = getToken()
      if (newToken) return newToken
    } catch (loginError) {
      console.error("Anche re-login fallito:", loginError)
    }

    return handleSessionExpired()
  }
}

// Mutex per evitare login concorrenti
let loginPromise: Promise<void> | null = null

export async function login(forceNewLogin?: boolean): Promise<void> {
  if (loginPromise && !forceNewLogin) {
    console.log("login() gia' in corso, attendo completamento...")
    return loginPromise
  }

  loginPromise = _loginInternal(forceNewLogin).finally(() => {
    loginPromise = null
  })
  return loginPromise
}

async function _loginInternal(forceNewLogin?: boolean): Promise<void> {
  if (AUTH_MODE === "production") {
    // Produzione: leggi i parametri dall'URL (passati dal portale via iframe)
    // Usa direttamente le variabili in memoria, senza dipendere da localStorage
    // (localStorage potrebbe essere bloccato in contesto iframe terze parti)
    const urlParams = new URLSearchParams(window.location.search)
    const urlToken = urlParams.get("token")
    const encodedParams = urlParams.get("p")

    console.log("=== IFRAME AUTH: Parametri ricevuti ===")
    console.log("URL completo:", window.location.href)
    console.log("Token nell'URL:", urlToken ? `${urlToken.substring(0, 50)}... (${urlToken.length} chars)` : "NON PRESENTE")
    console.log("Parametro p (base64):", encodedParams ? `${encodedParams.substring(0, 50)}... (${encodedParams.length} chars)` : "NON PRESENTE")

    let token = urlToken || ""
    let utente = ""
    let azienda = ""

    if (encodedParams) {
      try {
        const decodedString = atob(encodedParams)
        console.log("Parametro p decodificato:", decodedString)

        const parsed: Record<string, string> = {}
        decodedString.split(",").forEach((pair) => {
          const [key, ...rest] = pair.split(":").map((s) => s.trim())
          if (key) parsed[key] = rest.join(":")
        })
        console.log("Parametri parsati:", parsed)

        utente = parsed.username || parsed.utente || ""
        azienda = parsed.azienda_sysdat || parsed.azienda || ""
      } catch (e) {
        console.error("Errore nella decodifica del parametro p:", e)
      }
    }

    // AR20260616 — Cattura l'azienda PRECEDENTE da localStorage PRIMA di sovrascriverla
    // coi valori nuovi dell'URL. Serve a rilevare il cambio azienda anche quando il lock
    // `registerkc_done_azienda` non è mai stato scritto (es. registerkc di boot fallita):
    // col solo lock il confronto non scatterebbe mai.
    let prevAziendaLS = ""
    try {
      prevAziendaLS = localStorage.getItem("azienda_sysdat") || localStorage.getItem("azienda") || ""
    } catch { /* iframe */ }

    // Salva in cache in memoria (per registerToken durante refresh)
    if (token) tokenCache = token
    if (utente) utenteCache = utente
    if (azienda) aziendaCache = azienda

    // Tenta anche salvataggio in localStorage (potrebbe fallire in iframe terze parti)
    try {
      if (token) localStorage.setItem("token", token)
      if (utente) localStorage.setItem("username", utente)
      if (azienda) localStorage.setItem("azienda_sysdat", azienda)
    } catch {
      console.warn("localStorage non disponibile (contesto iframe terze parti)")
    }

    // Se non ci sono valori dall'URL, prova dalla cache/localStorage
    if (!token) token = getToken()
    if (!utente) utente = getUtente()
    if (!azienda) azienda = getAzienda()

    console.log("=== IFRAME AUTH: Valori per registerkc ===")
    console.log("token:", token ? `${token.substring(0, 50)}... (${token.length} chars)` : "VUOTO")
    console.log("utente:", utente || "VUOTO")
    console.log("azienda:", azienda || "VUOTO")

    if (!token || !utente || !azienda) {
      console.warn("Valori mancanti per registerkc — redirect alla pagina di login")
      return
    }

    console.log("[AUTH DEBUG] token+utente+azienda OK, procedo a lock check")

    // Evita registerKC duplicata cross-iframe.
    // Usa localStorage come lock condiviso tra iframe diversi (portale + app)
    // perché le variabili in memoria sono isolate per ogni contesto JS.
    // NB: il lock vale solo se anche utente+azienda coincidono con quelli registrati;
    // se è cambiata l'azienda, dobbiamo rifare registerkc per riallineare il backend.
    if (!forceNewLogin) {
      console.log("[AUTH DEBUG] forceNewLogin=false, tento lock check su localStorage")
      try {
        console.log("[AUTH DEBUG] sto per chiamare localStorage.getItem('registerkc_done_token')")
        const registeredToken = localStorage.getItem("registerkc_done_token")
        console.log("[AUTH DEBUG] registeredToken letto:",
          registeredToken ? `${registeredToken.substring(0, 40)}... (${registeredToken.length} chars)` : registeredToken)
        const registeredAzienda = localStorage.getItem("registerkc_done_azienda") || ""
        console.log("[AUTH DEBUG] registeredAzienda letto:", registeredAzienda)
        const registeredUtente = localStorage.getItem("registerkc_done_utente") || ""
        console.log("[AUTH DEBUG] registeredUtente letto:", registeredUtente)
        console.log("[AUTH DEBUG] match? token=", registeredToken === token,
          " azienda=", registeredAzienda === azienda,
          " utente=", registeredUtente === utente)
        if (
          registeredToken === token &&
          registeredAzienda === azienda &&
          registeredUtente === utente
        ) {
          console.log("registerKC: token gia' registrato per questa azienda/utente (localStorage lock), skip")
          registerkcDone = true
          saveAuthStateSnapshot()
          return
        }
        // AR20260616 — Token URL diverso ma azienda+utente combaciano col lock.
        // Un altro iframe (es. accettazioni) ha appena rinfrescato e registrato un token
        // per QUESTA azienda/utente (lock cross-iframe condiviso). Il token passato
        // nell'URL è invece quello "vecchio" del portale, ancora mappato a backend alla
        // VECCHIA azienda → ri-registrarlo darebbe OK ma servirebbe i dati della vecchia
        // azienda. Adottiamo il token del lock (già valido e registrato per l'azienda
        // corrente) e saltiamo la registerkc.
        if (
          registeredToken &&
          registeredToken !== token &&
          registeredAzienda === azienda &&
          registeredUtente === utente
        ) {
          console.warn(
            "registerKC: token URL diverso ma azienda/utente combaciano col lock → adotto il token del lock " +
            `(già registrato per azienda="${azienda}" utente="${utente}"), salto registerkc`
          )
          setToken(registeredToken)
          registerkcDone = true
          saveAuthStateSnapshot()
          return
        }
        if (registeredToken === token && (registeredAzienda !== azienda || registeredUtente !== utente)) {
          console.warn(
            `registerKC: stesso token ma azienda/utente cambiati (was azienda="${registeredAzienda}" utente="${registeredUtente}", ` +
            `now azienda="${azienda}" utente="${utente}") → re-registro per riallineare backend`
          )
        }
      } catch (lockErr) {
        console.warn("[AUTH DEBUG] lock check su localStorage ha LANCIATO:", lockErr)
      }
    } else {
      console.log("[AUTH DEBUG] forceNewLogin=true, salto lock check")
    }

    // AR20260616 — Cambio azienda al boot: refreshkc(token nuovo) + registerkc.
    // Quando l'utente cambia azienda nel portale, l'iframe viene ricaricato con un
    // NUOVO token (per la nuova azienda). Se l'azienda corrente (param p) differisce
    // da quella dell'ultima registrazione salvata nel lock cross-iframe, dobbiamo:
    //   1. refreshkc col token NUOVO dell'URL → token di sessione rinfrescato;
    //   2. registerkc di quel token con l'azienda/utente correnti.
    // Solo cambio azienda rilevato: al primo accesso il lock è vuoto → si salta e
    // si procede con la registerkc semplice sotto. Su forceNewLogin (re-login dopo
    // refresh fallito) si salta per evitare ricorsioni.
    if (!forceNewLogin) {
      let lockedAzienda = ""
      try {
        lockedAzienda = localStorage.getItem("registerkc_done_azienda") || ""
      } catch { /* iframe */ }

      // Rileva il cambio azienda col lock se presente, altrimenti con l'azienda
      // precedente letta da localStorage prima della sovrascrittura (lock vuoto).
      const previousAzienda = lockedAzienda || prevAziendaLS

      if (previousAzienda && previousAzienda !== azienda) {
        console.group("%c[AUTH] Cambio azienda rilevato al boot → refreshkc + registerkc col token nuovo", "color:#ff9800;font-weight:bold")
        console.warn("Azienda registrata in precedenza:", previousAzienda, lockedAzienda ? "(da lock)" : "(da localStorage)")
        console.warn("Azienda corrente (param p)      :", azienda)
        console.warn("Token nuovo (URL) da rinfrescare:", token ? `${token.substring(0, 40)}... (${token.length} chars)` : "(vuoto)")
        console.groupEnd()

        // Allinea la cache così registerToken (legge getToken/getAzienda/getUtente)
        // e le chiamate successive vedano il token nuovo e l'azienda corrente.
        setToken(token)
        registerkcDone = false

        try {
          const refreshResp = await fetch(`${API_URL}/refreshkc`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ token }),
          })
          if (!refreshResp.ok) throw new Error(`refreshkc HTTP ${refreshResp.status}`)
          const refreshData = await refreshResp.json()
          if (refreshData?.esito === "KO") throw new Error(refreshData?.messaggio || "refreshkc KO")
          if (!refreshData?.token) throw new Error("refreshkc: token mancante in risposta")

          // Registra il token rinfrescato con la nuova azienda/utente correnti.
          const finalToken = await registerToken(refreshData.token)

          // Aggiorna il lock cross-iframe con la nuova azienda/utente, così i reload
          // successivi con gli stessi parametri saltano del tutto questo giro.
          try {
            localStorage.setItem("registerkc_done_token", finalToken)
            localStorage.setItem("registerkc_done_azienda", azienda)
            localStorage.setItem("registerkc_done_utente", utente)
          } catch { /* iframe */ }

          console.group("%c[AUTH] Cambio azienda: refresh + register completato", "color:#4caf50;font-weight:bold")
          console.log("Token finale registrato:", finalToken ? `${finalToken.substring(0, 40)}...` : "(vuoto)")
          console.log("Azienda ora registrata :", azienda)
          console.log("Utente  ora registrato :", utente)
          console.groupEnd()
          return
        } catch (switchErr) {
          console.error("[AUTH] Cambio azienda: refreshkc/registerkc FALLITO:", switchErr)
          // Non restiamo in stato disallineato (backend vecchia azienda, client nuova):
          // invalidiamo e lasciamo che AuthGuard rediriga al login.
          handleSessionExpired()
        }
      }
    }

    console.log("[AUTH DEBUG] sto per chiamare fetch", `${API_URL}/registerkc`)
    const response = await fetch(`${API_URL}/registerkc`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token, utente, azienda }),
    })
    console.log("[AUTH DEBUG] fetch /registerkc completata, status:", response.status)
    try {
      const rawBody = await response.clone().text()
      console.log("[AUTH DEBUG] /registerkc raw response body:", rawBody)
    } catch (e) {
      console.warn("[AUTH DEBUG] impossibile leggere clone response body:", e)
    }

    if (!response.ok) {
      const err = await response.text()
      throw new Error(`Errore registerkc: ${err}`)
    }

    const data = await response.json()
    if (data?.esito === "KO") {
      // Chiave duplicata = token già registrato a backend
      if (data?.messaggio?.includes("unique key violation")) {
        // Se il contesto (azienda/utente) è cambiato rispetto all'ultima registrazione,
        // NON possiamo riusare il token: il backend ha ancora la vecchia mappatura
        // token→azienda e rifiuterà i dati. Forziamo refreshkc per ottenere un nuovo
        // token (diverso → niente unique violation) e registriamo quello con la
        // nuova azienda.
        let lockedAzienda = ""
        let lockedUtente = ""
        try {
          lockedAzienda = localStorage.getItem("registerkc_done_azienda") || ""
          lockedUtente = localStorage.getItem("registerkc_done_utente") || ""
        } catch { /* iframe */ }

        if ((lockedAzienda && lockedAzienda !== azienda) || (lockedUtente && lockedUtente !== utente)) {
          console.group("%c[AUTH] Chiave duplicata + cambio contesto → refreshkc per nuovo token", "color:#ff9800;font-weight:bold")
          console.warn("Azienda registrata in precedenza:", lockedAzienda)
          console.warn("Azienda corrente               :", azienda)
          console.warn("Utente  registrato in precedenza:", lockedUtente)
          console.warn("Utente  corrente               :", utente)
          console.warn("Token PRIMA della refresh      :", token)
          console.groupEnd()

          // Aggiorna cache così le chiamate successive vedono il token attuale
          setToken(token)
          registerkcDone = false

          try {
            const refreshResp = await fetch(`${API_URL}/refreshkc`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ token }),
            })
            if (!refreshResp.ok) throw new Error(`refreshkc HTTP ${refreshResp.status}`)
            const refreshData = await refreshResp.json()
            if (refreshData?.esito === "KO") throw new Error(refreshData?.messaggio)
            if (!refreshData?.token) throw new Error("refreshkc: token mancante in risposta")

            // Registra il NUOVO token col contesto corrente (registerToken
            // legge utente/azienda da getUtente()/getAzienda() e salva snapshot).
            const finalToken = await registerToken(refreshData.token)

            // Aggiorna il lock cross-iframe per il token URL corrente con la nuova
            // azienda/utente: al prossimo reload con stessi parametri eviteremo di
            // rifare il giro refreshkc + registerkc.
            try {
              localStorage.setItem("registerkc_done_token", token)
              localStorage.setItem("registerkc_done_azienda", azienda)
              localStorage.setItem("registerkc_done_utente", utente)
            } catch { /* iframe */ }

            console.group("%c[AUTH] Refresh + register completato con successo", "color:#4caf50;font-weight:bold")
            console.log("Token DOPO la refresh   :", finalToken)
            console.log("Token cambiato?         :", finalToken !== token ? "SÌ" : "NO (inatteso)")
            console.log("Azienda ora registrata  :", azienda)
            console.log("Utente  ora registrato  :", utente)
            console.groupEnd()
            return
          } catch (refreshErr) {
            console.error("[AUTH] Refresh dopo chiave duplicata FALLITO:", refreshErr)
            // Non riusciamo a riallineare: meglio invalidare il contesto e
            // lasciare che AuthGuard rediriga al login, anziché restare in uno
            // stato silenziosamente rotto.
            handleSessionExpired()
          }
        }

        console.log("registerKC: chiave duplicata, token gia' registrato a backend — uso token esistente")
        setToken(token)
        registerkcDone = true
        try {
          localStorage.setItem("registerkc_done_token", token)
          localStorage.setItem("registerkc_done_azienda", azienda)
          localStorage.setItem("registerkc_done_utente", utente)
        } catch { /* iframe */ }
        saveAuthStateSnapshot()
        return
      }
      throw new Error(data?.messaggio)
    }
    // Bug-fix: in alcune risposte di /registerkc il backend non restituisce `data.token`.
    // Prima facevamo `setToken(data.token)` → undefined → setToken azzerava tokenCache
    // e rimuoveva localStorage["token"] (validazione interna di setToken), lasciando
    // l'app senza sid utilizzabile. Usiamo come fallback il token URL già validato a
    // backend (registerkc ha appena risposto OK).
    const finalRegisteredToken = data.token || token
    console.log("[AUTH DEBUG] registerkc OK — data.token=", data.token ?? "(assente)",
      " → uso finalRegisteredToken di lunghezza", finalRegisteredToken?.length ?? 0)
    setToken(finalRegisteredToken)
    registerkcDone = true
    // Salva lock in localStorage per evitare registerKC duplicata da altri iframe;
    // includiamo azienda e utente per invalidare il lock se il portale cambia contesto.
    try {
      localStorage.setItem("registerkc_done_token", finalRegisteredToken)
      localStorage.setItem("registerkc_done_azienda", azienda)
      localStorage.setItem("registerkc_done_utente", utente)
    } catch { /* localStorage non disponibile */ }
    saveAuthStateSnapshot()
  } else {
    // Sviluppo: il login passa SEMPRE dalla pagina di login (Login.tsx → useLogin).
    // Qui ci rifacciamo solo carico di rileggere il token già salvato in localStorage
    // (es. al boot dell'app); se non c'è, lasciamo che AuthGuard rediriga alla login.
    if (!forceNewLogin) {
      const storedToken = getToken()
      if (storedToken) {
        tokenCache = storedToken
        // Inizializza lo snapshot col contesto già presente in localStorage,
        // così i successivi cambi di azienda/utente verranno rilevati da fetchGuard.
        saveAuthStateSnapshot()
        return
      }
    }

    // Path legacy: auto-login dev con DEV_USER/PASSWORD/AZIENDA se presenti in
    // config runtime. Mantenuto per retrocompatibilità del comportamento esistente.
    if (DEV_USER && DEV_PASSWORD && DEV_AZIENDA) {
      const payload = {
        utente: DEV_USER,
        password: DEV_PASSWORD,
        azienda: DEV_AZIENDA,
      }

      const response = await fetch(`${API_URL}/loginkc`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      })

      if (!response.ok) {
        const err = await response.text()
        throw new Error(`Errore loginkc: ${err}`)
      }

      if ((await response.clone().text()).trim().startsWith("<")) {
        throw new Error("Invalid API response")
      }

      const data = await response.json()
      if (data?.esito === "KO") throw new Error(data?.messaggio)
      setToken(data.token)
      setUtente(DEV_USER)
      setAzienda(DEV_AZIENDA)
      saveAuthStateSnapshot()
    }
    // Niente DEV_USER/PASSWORD configurati e niente token salvato:
    // AuthGuard reindirizzerà alla pagina di login.
  }
}
